import { Flex } from "@chakra-ui/react";
import { useIntl } from "react-intl";

import { RanaLoading, RanaModal } from "@nens/ranalib";
import { FormErrors, RanaForm, SubmitFunction } from "@nens/ranalib";

import { cancelButtonText } from "@/translations";
import { ProjectFileOrDirectory } from "@/types/api-model-simple-names";
import { containsInvalidCharacters, getFirstInvalidCharacter } from "@/utils/invalid-characters";

import { useAddFolder } from "../api/add-folder";
import { MoveProjectFileOrDirectoryMutation } from "../api/move";

interface AddFolderModalProps {
  basePath: string;
  existingFileIds: string[];
  items: ProjectFileOrDirectory[];
  onClose: () => void;
  moveProjectFileOrDirectoryMutation: MoveProjectFileOrDirectoryMutation;
}

type FolderFormValues = {
  folderName: string;
};

export function AddFolderWithSelectionModal({
  items,
  basePath,
  onClose,
  existingFileIds,
  moveProjectFileOrDirectoryMutation,
}: AddFolderModalProps) {
  const intl = useIntl();
  const addFolderMutation = useAddFolder(basePath);
  const isMutating = addFolderMutation.isMutating || moveProjectFileOrDirectoryMutation.isMutating;

  const validate = ({ folderName }: FolderFormValues): FormErrors => {
    if (existingFileIds.includes(basePath + folderName + "/")) {
      return {
        folderName: intl.formatMessage({
          id: "foldererrors.name_existed",
          defaultMessage: "Folder name already exists",
        }),
      };
    }

    const notAllowedCharacter = containsInvalidCharacters(folderName);
    if (notAllowedCharacter)
      return {
        folderName: intl.formatMessage(
          {
            id: "foldererrors.not_allowed_character",
            defaultMessage: `Character "{character}" is not allowed`,
          },
          {
            character: getFirstInvalidCharacter(folderName),
          }
        ),
      };

    return {};
  };

  const submit: SubmitFunction<FolderFormValues> = async (validatedValues, onError) => {
    addFolderMutation.mutate(validatedValues.folderName, {
      onSuccess: () => {
        const folderPath = basePath + validatedValues.folderName + "/";
        moveProjectFileOrDirectoryMutation.mutateWithConfirm(
          { items, destination: folderPath },
          { onSettled: () => onClose() }
        );
      },
      onError,
    });
  };

  const topBarText = intl.formatMessage({
    id: "form.new_folder_name",
    defaultMessage: "New folder name",
  });

  const submitButtonText = intl.formatMessage(
    {
      id: "button.move_into_new_folder",
      defaultMessage: "{count, plural, one {Move # item} other {Move # items}}",
    },
    { count: items.length }
  );

  return (
    <RanaForm validate={validate} doSubmit={submit}>
      <RanaModal
        isOpen
        isCentered
        onClose={onClose}
        topBar={topBarText}
        buttons={[
          {
            children: cancelButtonText,
            variant: "tertiary",
            isDisabled: isMutating,
            onClick: onClose,
          },
          <RanaForm.SubmitButton key="submit" isDisabled={isMutating}>
            {submitButtonText}
          </RanaForm.SubmitButton>,
        ]}
      >
        <Flex flexDir="column" gap={2}>
          <RanaForm.Input submitOnEnter autoFocus field="folderName" isRequired />
          {isMutating && <RanaLoading size="md" isCentered />}
          <RanaForm.Errors />
        </Flex>
      </RanaModal>
    </RanaForm>
  );
}
